import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import { SocialHubService } from '../social-feed/services/social-hub.service';
import { SocialNotification } from '../social-feed/models/social-feed.model';

/** Every notification, newest first, with "Mark all read". */
@Component({
  selector: 'app-social-notifications',
  templateUrl: './social-notifications.component.html',
  styleUrls: ['./social-notifications.component.scss'],
  standalone: false,
})
export class SocialNotificationsComponent implements OnInit {
  items: SocialNotification[] = [];
  loading = true;
  failed = false;
  page = 1;
  hasMore = false;
  loadingMore = false;
  filter: 'all' | 'unread' = 'all';

  constructor(
    private social: SocialFeedService,
    private hub: SocialHubService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  get shown(): SocialNotification[] {
    return this.filter === 'unread' ? this.items.filter((n) => !n.is_read) : this.items;
  }

  get unread(): number {
    return this.items.filter((n) => !n.is_read).length;
  }

  load(more = false): void {
    const page = more ? this.page + 1 : 1;
    if (more) this.loadingMore = true;
    else {
      this.loading = true;
      this.failed = false;
    }
    this.social.getNotifications(page).subscribe({
      next: (res) => {
        const have = new Set(more ? this.items.map((n) => n.id) : []);
        this.items = more ? [...this.items, ...res.results.filter((n) => !have.has(n.id))] : res.results;
        this.page = page;
        this.hasMore = !!res.next;
        this.loading = this.loadingMore = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = this.loadingMore = false;
        this.failed = !more;
        this.cdr.markForCheck();
      },
    });
  }

  open(n: SocialNotification): void {
    this.hub.markNotificationRead(n);
    const target = SocialHubService.notificationTarget(n);
    if (target) this.router.navigate(target.commands, { queryParams: target.queryParams });
  }

  markAll(): void {
    this.items.forEach((n) => (n.is_read = true));
    this.hub.markAllNotificationsRead();
  }

  icon(n: SocialNotification): string {
    switch (n.notif_type) {
      case 'like': return 'fa-heart';
      case 'comment': return 'fa-comment';
      case 'follow': return 'fa-user-plus';
      case 'new_post': return 'fa-pencil';
      case 'community_invite': return 'fa-envelope-open';
      case 'community_request': return 'fa-user-plus';
      case 'community': return 'fa-users';
      default: return 'fa-bell';
    }
  }

  avatar(n: SocialNotification): string {
    return n.actor?.avatar ? this.social.resolveImageUrl(n.actor.avatar) : '';
  }

  initials(name: string | undefined): string {
    return (name || '').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }

  timeAgo(isoDate: string): string {
    const minutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d` : new Date(isoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
